import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";

// Validation schema
const validationSchema = Yup.object({
  note: Yup.string()
    .required("Note text is required")
    .min(1, "Note cannot be empty"),
  title: Yup.string().optional(),
});

const NotesForm = ({ dealId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingNotes, setFetchingNotes] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isFormExpanded, setIsFormExpanded] = useState(false);

  // Popup states
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [noteDescription, seNoteDescription] = useState("");
  console.log(noteDescription, "-0-0");

  const userId = localStorage.getItem("userId");

  // Fetch notes when dealId changes
  useEffect(() => {
    if (dealId) {
      fetchNotes();
    }
  }, [dealId]);

  const fetchNotes = async () => {
    try {
      setFetchingNotes(true);
      setError(null);

      const response = await axiosInstance.get(
        `${apiEndPointsConfig.getAllNotes}/${dealId}`
      );

      setNotes(response.data);
    } catch (err) {
      setError("Failed to load notes. Please try again.");
      console.error("Error fetching notes:", err);
    } finally {
      setFetchingNotes(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      note: "",
      title: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  const addDealTimeline = async (payload) => {
    
    try {
      await axiosInstance.post(apiEndPointsConfig.createTimeline, payload);
    } catch (error) {
      console.error("Error creating deal timeline:", error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      const notePayload = {
        dealId,
        note: values.note,
        title: values.title,
        userId,
      };

      const timelinePayload = {
        dealId,
        title: "Note added",
        description: values.note,
        userId,
        changeTime: new Date(),
      };

      const response = await axiosInstance.post(
        apiEndPointsConfig.addNotes,
        notePayload
      );

      const noteData = response.data.note || response.data;

      setNotes((prevNotes) => [noteData, ...prevNotes]);

      addDealTimeline(timelinePayload);

      formik.resetForm();
      setIsFormExpanded(false);
      setSuccessMessage("Note saved successfully!");

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError("Failed to save note. Please try again.");
      console.error("Error creating note:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const timelinePayload = {
        dealId,
        title: "Note Deleted",
        description: noteDescription,
        userId,
        changeTime: new Date(),
      };
      const response = await axiosInstance.delete(
        `${apiEndPointsConfig.deleteNotes}/${noteId}`
      );

      setNotes(notes.filter((note) => note._id !== noteId));
      addDealTimeline(timelinePayload);

      setSuccessMessage("Note deleted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError("Failed to delete note. Please try again.");
      console.error("Error deleting note:", err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "U");

  if (!dealId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Please select a deal to add notes</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", px: { xs: 0, sm: 1 }, py: 1 }}>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Note Form */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          bgcolor: "#f9fafb",
          p: 2,
          mb: 3,
          border: "1px solid #e5e7eb",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
          }}
        >
          <Box
            sx={{
              bgcolor: "#eff6ff",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoteAltIcon sx={{ fontSize: 18, color: "#2563eb" }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Add a note
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Keep track of important updates related to this deal.
            </Typography>
          </Box>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            placeholder="Write your note here..."
            name="note"
            value={formik.values.note}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            onFocus={() => setIsFormExpanded(true)}
            variant="outlined"
            disabled={loading}
            error={formik.touched.note && !!formik.errors.note}
            helperText={formik.touched.note && formik.errors.note}
            multiline
            minRows={isFormExpanded ? 3 : 1}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#ffffff",
                borderRadius: 2,
              },
            }}
          />

          <Collapse in={isFormExpanded} timeout="auto">
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a title (optional)"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#ffffff",
                    borderRadius: 2,
                  },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    formik.resetForm();
                    setIsFormExpanded(false);
                  }}
                  disabled={loading}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  disabled={loading || !formik.values.note.trim()}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    "Save Note"
                  )}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </form>
      </Paper>

      {/* Notes List */}
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            All Notes ({notes.length})
          </Typography>
        </Box>

        {fetchingNotes ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : notes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px dashed #e5e7eb",
              bgcolor: "#f9fafb",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontStyle: "italic" }}
            >
              No notes yet. Add one to get started.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {notes.map((note) => (
              <Paper
                key={note._id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  bgcolor: "#ffffff",
                  display: "flex",
                  gap: 1.5,
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    bgcolor: "#22c55e",
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#22c55e",
                    width: 32,
                    height: 32,
                    mt: 0.5,
                  }}
                >
                  {getInitial(note.userName || "U")}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {note.userName || "Unknown"}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "#6b7280", mt: 0.25 }}
                      >
                        {formatDate(note.createdAt)}
                      </Typography>
                    </Box>

                    <Tooltip title="Delete note">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNoteToDelete(note._id);
                          seNoteDescription(note.note);
                          setOpenDeletePopup(true);
                        }}
                        sx={{
                          color: "#ef4444",
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {note.title && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        mt: 1,
                        fontSize: 14,
                      }}
                    >
                      {note.title}
                    </Typography>
                  )}

                  <Typography sx={{ mt: 0.75, fontSize: 14 }}>
                    {note.note}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Popup */}
      <Dialog
        open={openDeletePopup}
        onClose={() => setOpenDeletePopup(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Delete Note?
        </DialogTitle>

        <Divider sx={{ m: 0 }} />

        <DialogContent sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setOpenDeletePopup(false)}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={() => {
              handleDeleteNote(noteToDelete);
              setOpenDeletePopup(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesForm;
